import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowDown, ArrowUp, Package, Filter, ChevronLeft, ChevronRight, AlertTriangle, RefreshCw, LogIn, Plus, X } from 'lucide-react';
import { inventarioService } from '@/services/inventario.service';
import { productosService } from '@/services/productos.service';
import { sucursalesService } from '@/services/sucursales.service';
import { useAuthStore } from '@/stores/auth-store';
import { dialogs } from '@/stores/dialog-store';

const typeLabels: Record<string, string> = {
  IN: 'Entrada',
  OUT: 'Salida',
  ADJUSTMENT: 'Ajuste',
};

const typeColors: Record<string, string> = {
  IN: 'bg-ios-green/10 text-ios-green',
  OUT: 'bg-red-50 text-ios-red',
  ADJUSTMENT: 'bg-yellow-50 text-yellow-600',
};

const typeIcons: Record<string, any> = {
  IN: ArrowDown,
  OUT: ArrowUp,
  ADJUSTMENT: RefreshCw,
};

export function KardexPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [movements, setMovements] = useState<any[]>([]);
  const [product, setProduct] = useState<any>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'auth' | 'network' | 'unknown' | null>(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustData, setAdjustData] = useState({ type: 'OUT', quantity: 1, reason: '' });
  const [adjusting, setAdjusting] = useState(false);
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const limit = 50;

  useEffect(() => {
    if (user?.company?.id) {
      sucursalesService.findByCompany(user.company.id).then(b => {
        setBranches(b);
      }).catch(() => {});

      productosService.findAll({ companyId: user.company.id, limit: 200 }).then(res => {
        const prods = Array.isArray(res) ? res : res?.items || res?.products || res?.data || [];
        setAllProducts(prods);
      }).catch(() => {});
    }
  }, [user?.company?.id]);

  useEffect(() => {
    if (user?.company?.id) {
      loadKardex();
      if (productId && productId !== 'all') {
        loadProductInfo();
      } else {
        setProduct(null);
      }
    }
  }, [productId, user, page, typeFilter, selectedBranchId]);

  const handleAuthError = async () => {
    logout();
    navigate('/login', { replace: true });
  };

  const loadKardex = async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    setError(null);
    setErrorType(null);
    try {
      const targetProductId = (productId && productId !== 'all') ? productId : undefined;
      const result = await inventarioService.getMovements(targetProductId, user.company.id, {
        type: typeFilter || undefined,
        branchId: selectedBranchId || undefined,
        page,
        limit,
      });
      setMovements(result.data || []);
      setTotal(result.total || 0);
      setTotalPages(result.totalPages || 1);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setError('Tu sesión ha expirado. Inicia sesión nuevamente para ver el Kardex.');
        setErrorType('auth');
      } else if (err?.message === 'Network Error' || !err?.response) {
        setError('No se pudo conectar con el servidor. Verifica que el backend esté corriendo.');
        setErrorType('network');
      } else {
        setError(err?.response?.data?.message || 'Error al cargar los movimientos de inventario.');
        setErrorType('unknown');
      }
    }
    setLoading(false);
  };

  const loadProductInfo = async () => {
    if (!user?.company?.id || !productId || productId === 'all') return;
    try {
      const info = await inventarioService.getStockInfo(productId, user.company.id, selectedBranchId || undefined);
      setProduct(info);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setError('Tu sesión ha expirado.');
        setErrorType('auth');
      }
    }
  };

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-ios-gray-6 transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold">
              {productId && productId !== 'all' ? 'Kardex de Inventario' : 'Movimientos de Inventario Global'}
            </h1>
            <p className="text-sm text-ios-gray mt-1">
              {productId && productId !== 'all'
                ? (product ? `${product.name} (${product.sku || 'Sin SKU'})` : 'Cargando producto...')
                : 'Bitácora general de todas las entradas, salidas y ajustes de productos'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Product selector */}
          <select
            value={productId || 'all'}
            onChange={(e) => navigate(`/kardex/${e.target.value}`)}
            className="ios-input h-10 text-sm font-medium border-ios-blue/30 focus:border-ios-blue max-w-xs"
          >
            <option value="all">📦 Todos los Productos (Historial General)</option>
            {allProducts.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.sku || p.barcode || 'Sin SKU'})
              </option>
            ))}
          </select>

          <button
            onClick={() => setShowAdjustModal(true)}
            disabled={!product}
            className="ios-button flex items-center gap-2"
          >
            <Plus size={18} />
            Ajustar Stock
          </button>
        </div>
      </div>

      {/* Stock summary cards */}
      {product ? (
        <div className="grid grid-cols-4 gap-4">
          <div className="ios-card p-4">
            <p className="text-xs text-ios-gray uppercase tracking-wider">Stock Actual</p>
            <p className={`text-2xl font-bold mt-1 ${product.stock <= (product.minStock || 0) ? 'text-ios-red' : product.stock === 0 ? 'text-ios-gray' : 'text-ios-green'}`}>
              {product.stock}
            </p>
            <p className="text-xs text-ios-gray mt-1">
              Mín: {product.minStock} {product.maxStock ? `/ Máx: ${product.maxStock}` : ''}
            </p>
          </div>
          <div className="ios-card p-4">
            <p className="text-xs text-ios-gray uppercase tracking-wider">Valorizado</p>
            <p className="text-2xl font-bold mt-1 text-ios-blue">
              ${Number(product.stockValue).toFixed(2)}
            </p>
            <div className="mt-2 bg-ios-gray-6/50 rounded-md p-2 border border-ios-gray-5 inline-block">
              <p className="text-sm text-ios-dark font-medium">
                Costo Promedio: <span className="font-bold text-ios-blue">${Number(product.costAverage).toFixed(2)}</span>
              </p>
            </div>
          </div>
          <div className="ios-card p-4">
            <p className="text-xs text-ios-gray uppercase tracking-wider">Entradas</p>
            <p className="text-2xl font-bold mt-1 text-ios-green">+{product.summary?.totalIn || 0}</p>
          </div>
          <div className="ios-card p-4">
            <p className="text-xs text-ios-gray uppercase tracking-wider">Salidas</p>
            <p className="text-2xl font-bold mt-1 text-ios-red">-{product.summary?.totalOut || 0}</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          <div className="ios-card p-4">
            <p className="text-xs text-ios-gray uppercase tracking-wider">Total Movimientos</p>
            <p className="text-2xl font-bold mt-1 text-ios-blue">{total}</p>
            <p className="text-xs text-ios-gray mt-1">Registrados en la bitácora</p>
          </div>
          <div className="ios-card p-4">
            <p className="text-xs text-ios-gray uppercase tracking-wider">Unidades Recibidas (Entradas)</p>
            <p className="text-2xl font-bold mt-1 text-ios-green">
              +{movements.filter(m => m.type === 'IN').reduce((acc, m) => acc + (m.quantity || 0), 0)}
            </p>
            <p className="text-xs text-ios-gray mt-1">Suma de entradas en esta página</p>
          </div>
          <div className="ios-card p-4">
            <p className="text-xs text-ios-gray uppercase tracking-wider">Unidades Retiradas (Salidas)</p>
            <p className="text-2xl font-bold mt-1 text-ios-red">
              -{movements.filter(m => m.type === 'OUT').reduce((acc, m) => acc + (m.quantity || 0), 0)}
            </p>
            <p className="text-xs text-ios-gray mt-1">Suma de salidas en esta página</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <select
          value={selectedBranchId}
          onChange={(e) => setSelectedBranchId(e.target.value)}
          className="ios-input max-w-xs h-9 text-sm"
        >
          <option value="">Todas las sucursales</option>
          {branches.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="ios-input max-w-xs h-9 text-sm"
        >
          <option value="">Todos los movimientos</option>
          <option value="IN">Solo Entradas (+)</option>
          <option value="OUT">Solo Salidas (-)</option>
          <option value="ADJUSTMENT">Solo Ajustes</option>
        </select>

        <div className="ml-auto text-xs text-ios-gray">
          {total} movimientos en total
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="ios-card p-12 text-center">
          {errorType === 'auth' ? (
            <>
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogIn size={28} className="text-ios-red" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Sesión Caducada</h3>
              <p className="text-sm text-ios-gray mb-6 max-w-md mx-auto">{error}</p>
              <button onClick={handleAuthError} className="ios-button flex items-center gap-2 mx-auto">
                Iniciar Sesión
              </button>
            </>
          ) : errorType === 'network' ? (
            <>
              <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={28} className="text-yellow-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Error de conexión</h3>
              <p className="text-sm text-ios-gray mb-6 max-w-md mx-auto">{error}</p>
              <button onClick={() => { loadKardex(); loadProductInfo(); }} className="ios-button flex items-center gap-2 mx-auto">
                <RefreshCw size={16} />
                Reintentar
              </button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={28} className="text-ios-red" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Error</h3>
              <p className="text-sm text-ios-gray mb-6 max-w-md mx-auto">{error}</p>
              <button onClick={() => { loadKardex(); loadProductInfo(); }} className="ios-button flex items-center gap-2 mx-auto">
                <RefreshCw size={16} />
                Reintentar
              </button>
            </>
          )}
        </div>
      )}

      {!error && (
        <div className="ios-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Fecha</th>
                {(!productId || productId === 'all') && (
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Producto</th>
                )}
                <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Tipo</th>
                <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Referencia</th>
                <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Cantidad</th>
                <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Stock Antes</th>
                <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Stock Después</th>
                <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Costo</th>
                <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Usuario</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ios-gray-5">
              {loading ? (
                <tr>
                  <td colSpan={(!productId || productId === 'all') ? 9 : 8} className="text-center py-12">
                    <div className="w-6 h-6 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : movements.length === 0 ? (
                <tr>
                  <td colSpan={(!productId || productId === 'all') ? 9 : 8} className="text-center py-12 text-ios-gray text-sm">
                    No hay movimientos registrados
                  </td>
                </tr>
              ) : (
                movements.map((m: any) => {
                  const Icon = typeIcons[m.type] || Package;
                  const isOut = m.type === 'OUT';
                  const prodName = m.productName || m.product?.name || 'Producto';
                  const prodSku = m.productSku || m.product?.sku || m.productBarcode || m.product?.barcode;

                  return (
                    <tr key={m.id} className="hover:bg-ios-gray-6/30 transition-colors">
                      <td className="px-4 py-3 text-xs text-ios-gray whitespace-nowrap">
                        {new Date(m.createdAt).toLocaleDateString('es-MX', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </td>
                      {(!productId || productId === 'all') && (
                        <td className="px-4 py-3 text-xs font-medium text-ios-blue hover:underline cursor-pointer" onClick={() => navigate(`/kardex/${m.productId}`)}>
                          {prodName}
                          {prodSku && <span className="block text-[10px] text-ios-gray font-normal">{prodSku}</span>}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${typeColors[m.type] || ''}`}>
                          <Icon size={10} />
                          {typeLabels[m.type] || m.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {m.reference ? (
                          <span className="text-ios-gray">{m.reference}</span>
                        ) : (
                          <span className="text-ios-gray">—</span>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-right font-semibold ${isOut ? 'text-ios-red' : 'text-ios-green'}`}>
                        {isOut ? '' : '+'}{m.quantity}
                      </td>
                      <td className="px-4 py-3 text-right text-ios-gray">{m.stockBefore}</td>
                      <td className="px-4 py-3 text-right font-medium">{m.stockAfter}</td>
                      <td className="px-4 py-3 text-right text-xs text-ios-gray">
                        {m.unitCost ? `$${Number(m.unitCost).toFixed(2)}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-ios-gray">
                        {m.createdBy?.fullName || '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-ios-gray-5">
            <span className="text-xs text-ios-gray">Página {page} de {totalPages}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg hover:bg-ios-gray-6 disabled:opacity-50">
                <ChevronLeft size={16} />
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg hover:bg-ios-gray-6 disabled:opacity-50">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
      )}
      {/* Modal de Ajuste */}
      {showAdjustModal && product && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
              <h3 className="font-semibold text-lg">Ajuste de Inventario</h3>
              <button onClick={() => setShowAdjustModal(false)} className="p-1 text-ios-gray hover:bg-ios-gray-6 rounded-full">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ios-dark mb-1">Tipo de Movimiento</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setAdjustData({ ...adjustData, type: 'IN' })}
                    className={`py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                      adjustData.type === 'IN'
                        ? 'bg-ios-green/10 border-ios-green text-ios-green'
                        : 'border-ios-gray-5 text-ios-gray hover:bg-ios-gray-6'
                    }`}
                  >
                    Entrada (+)
                  </button>
                  <button
                    onClick={() => setAdjustData({ ...adjustData, type: 'OUT' })}
                    className={`py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                      adjustData.type === 'OUT'
                        ? 'bg-ios-red/10 border-ios-red text-ios-red'
                        : 'border-ios-gray-5 text-ios-gray hover:bg-ios-gray-6'
                    }`}
                  >
                    Salida (-)
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ios-dark mb-1">Cantidad</label>
                <input
                  type="number"
                  min="1"
                  value={adjustData.quantity}
                  onChange={(e) => setAdjustData({ ...adjustData, quantity: parseInt(e.target.value) || 0 })}
                  className="ios-input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ios-dark mb-1">Motivo / Razón</label>
                <input
                  type="text"
                  placeholder="Ej. Producto dañado, Caducidad, Ajuste físico"
                  value={adjustData.reason}
                  onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })}
                  className="ios-input w-full"
                />
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowAdjustModal(false)}
                  className="flex-1 py-2.5 rounded-xl font-semibold bg-ios-gray-6 text-ios-dark hover:bg-ios-gray-5 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  disabled={adjusting || adjustData.quantity <= 0 || !adjustData.reason.trim()}
                  onClick={async () => {
                    setAdjusting(true);
                    try {
                      await inventarioService.adjust({
                        companyId: user!.company.id,
                        branchId: selectedBranchId,
                        productId: product.id,
                        quantity: adjustData.type === 'OUT' ? -Math.abs(adjustData.quantity) : Math.abs(adjustData.quantity),
                        reason: adjustData.reason
                      });
                      setShowAdjustModal(false);
                      setAdjustData({ type: 'OUT', quantity: 1, reason: '' });
                      loadKardex();
                      loadProductInfo();
                    } catch (e) {
                      console.error(e);
                      await dialogs.alert('Error al aplicar el ajuste');
                    } finally {
                      setAdjusting(false);
                    }
                  }}
                  className="flex-1 py-2.5 rounded-xl font-semibold bg-ios-blue text-white hover:bg-blue-600 disabled:opacity-50 transition-colors"
                >
                  {adjusting ? 'Guardando...' : 'Aplicar Ajuste'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
